import { isSelectionEmpty, toValueWithSelection } from '../helpers';
import Mask, { ValueWithSelection } from '../Mask';

import {
    DEFAULT_DECIMAL_PLACEHOLDER,
    DEFAULT_PLACEHOLDER,
    JAVASCRIPT_DECIMAL_SEPARATOR,
    NEGATIVE_SIGN,
    NUMBER_REGEX,
} from './constants';
import { formatNumber, getMaskedNumberRegex } from './helpers';
import { NumberMaskOptions } from './types';

export default class NumberMask extends Mask {
    private decimalSeparator: string;
    private alternativeDecimalSeparator: string;
    private groupSeparator: string;
    private groupSeparatorRegExp: RegExp;
    private maxPrecision: number;
    private groupSize: number;
    private allowNegativeNumber: boolean;
    private maskedNumberRegex: RegExp;

    constructor({
        decimalSeparator,
        groupSeparator,
        precision = 0,
        groupSize = 3,
        allowNegativeNumber = true,
        alternativeDecimalSeparator,
    }: NumberMaskOptions) {
        super();
        this.decimalSeparator = decimalSeparator;
        this.alternativeDecimalSeparator = alternativeDecimalSeparator;
        this.groupSeparator = groupSeparator;
        this.groupSeparatorRegExp = new RegExp(`\\${groupSeparator}`);
        this.maxPrecision = precision;
        this.groupSize = groupSize;
        this.allowNegativeNumber = allowNegativeNumber;
        this.maskedNumberRegex = getMaskedNumberRegex({
            allowNegativeNumber,
            groupSeparator,
            decimalSeparator,
            groupSize,
            precision,
        });
    }

    conforms(value: string): boolean {
        return this.maskedNumberRegex.test(value);
    }

    unmask({ value, selection }: ValueWithSelection): ValueWithSelection {
        const [integerPart, decimalPart] = value.split(this.decimalSeparator);

        let { start, end } = selection;
        let newValue = integerPart;
        let groupSeparatorIndex = integerPart.indexOf(this.groupSeparator);

        while (groupSeparatorIndex !== -1) {
            newValue = newValue.replace(this.groupSeparatorRegExp, '');
            if (groupSeparatorIndex < start) {
                start--;
            }
            if (groupSeparatorIndex < end) {
                end--;
            }
            groupSeparatorIndex = newValue.indexOf(this.groupSeparator);
        }

        if (decimalPart !== undefined) {
            newValue = `${newValue}${JAVASCRIPT_DECIMAL_SEPARATOR}${decimalPart}`;
        }

        return { value: newValue, selection: { start, end } };
    }

    input(valueWithSelection: ValueWithSelection, char: string): ValueWithSelection {
        let vs = valueWithSelection;

        if (!isSelectionEmpty(vs)) {
            vs = this.remove(vs);
        }

        if (NUMBER_REGEX.test(char) && this.isSpaceForSymbols(vs)) {
            vs = this.insertChar(char, vs);
            return vs;
        }

        if (this.allowNegativeNumber && char === NEGATIVE_SIGN && isSelectionEmpty(vs) && vs.selection.start === 0) {
            return {
                value: `${char}${vs.value}`,
                selection: { start: vs.selection.start + 1, end: vs.selection.end + 1 },
            };
        }

        if (
            this.maxPrecision > 0 &&
            this.isDecimalSeparator(char) &&
            !vs.value.includes(this.decimalSeparator) &&
            isPlaceForDecimalSeparator(vs)
        ) {
            return {
                value: `${vs.value}${this.decimalSeparator}`,
                selection: { start: vs.selection.start + 1, end: vs.selection.end + 1 },
            };
        }

        return vs;
    }

    remove(vs: ValueWithSelection): ValueWithSelection {
        if (isSelectionEmpty(vs)) {
            return vs;
        }

        const { start: originalStart, end: originalEnd } = vs.selection;

        // Skip group separator instead deleting it
        if (originalEnd - originalStart === 1 && vs.value[originalStart] === this.groupSeparator) {
            return { ...vs, selection: { start: originalStart, end: originalStart } };
        }

        const {
            value: unformattedValue,
            selection: { start, end },
        } = this.unmask(vs);

        const newUnformattedValue = `${unformattedValue.slice(0, start)}${unformattedValue.slice(end)}`;
        const newFormattedValue = formatNumber({
            vs: { value: newUnformattedValue, selection: { start, end: start } },
            groupSize: this.groupSize,
            groupSeparator: this.groupSeparator,
            decimalSeparator: this.decimalSeparator,
            maxPrecision: this.maxPrecision,
        });

        return this.conforms(newFormattedValue.value)
            ? this.keepDecimalSeparator(newUnformattedValue, newFormattedValue)
            : { ...vs, selection: { start: vs.selection.start, end: vs.selection.start } };
    }

    getPlaceholder(): string {
        return this.maxPrecision > 0
            ? DEFAULT_DECIMAL_PLACEHOLDER.padEnd(DEFAULT_DECIMAL_PLACEHOLDER.length + this.maxPrecision, '0')
            : DEFAULT_PLACEHOLDER;
    }

    // Inserts into unformatted value
    private insertChar(char: string, vs: ValueWithSelection): ValueWithSelection {
        const {
            value: unmaskedValue,
            selection: { start: unmaskedStart, end: unmaskedEnd },
        } = this.unmask(vs);
        const unmaskedValueChars = [...unmaskedValue];

        unmaskedValueChars.splice(unmaskedStart, 0, char);

        const formattedValue = formatNumber({
            vs: toValueWithSelection(unmaskedValueChars, unmaskedStart + 1, unmaskedEnd + 1),
            groupSize: this.groupSize,
            groupSeparator: this.groupSeparator,
            decimalSeparator: this.decimalSeparator,
            maxPrecision: this.maxPrecision,
        });

        return this.keepDecimalSeparator(unmaskedValueChars, formattedValue);
    }

    private isDecimalSeparator(char: string): boolean {
        return char === this.decimalSeparator || char === this.alternativeDecimalSeparator;
    }

    private isInputInDecimalPart({ value, selection }: ValueWithSelection): boolean {
        const decimalSeparatorIndex = value.indexOf(this.decimalSeparator);
        return this.maxPrecision > 0 && decimalSeparatorIndex !== -1 && selection.start > decimalSeparatorIndex;
    }

    private isSpaceForSymbols(vs: ValueWithSelection): boolean {
        if (this.isInputInDecimalPart(vs)) {
            const [, decimalPart] = vs.value.split(this.decimalSeparator);
            return decimalPart.length < this.maxPrecision;
        }
        return true;
    }

    private keepDecimalSeparator(unformattedValue: string | string[], vs: ValueWithSelection): ValueWithSelection {
        const newVs = { ...vs };

        if (this.isDecimalSeparator(unformattedValue[unformattedValue.length - 1])) {
            newVs.value = `${newVs.value}${this.decimalSeparator}`;
        }

        return newVs;
    }
}

const isPlaceForDecimalSeparator = ({ value, selection }: ValueWithSelection) =>
    ((!value.startsWith(NEGATIVE_SIGN) && selection.start > 0) || selection.start > 1) &&
    selection.start === value.length;
